import { Trans, useTranslation } from 'react-i18next';
import { ReactNode } from 'react';
import { MarketplacePostNoRevenue, useMarketplacePostsNoRevenueQuery } from '@anytag/graphql';
import { TableCell, TableV2 } from '@anytag/shared-atoms';
import { useGenerateInfluencerProfileLink } from '@anytag/shared-hooks';
import { InfluencerAvatar, PostsSection } from '@anytag/shared-molecules';
import { ColumnDef } from '@tanstack/react-table';

interface PostsWithoutRevenueProps {
  children?: ReactNode;
  marketplacePostIds: (number | null)[];
}
export const PostsWithoutRevenue = ({ marketplacePostIds, children }: PostsWithoutRevenueProps) => {
  const { t } = useTranslation();
  const { data } = useMarketplacePostsNoRevenueQuery({
    variables: { marketplacePostIds: marketplacePostIds.filter((p) => p !== null) as number[] }
  });
  const { generateProfileLink } = useGenerateInfluencerProfileLink();

  const columns: ColumnDef<MarketplacePostNoRevenue>[] = [
    {
      accessorKey: 'influencer',
      header: t<string>('HeaderColumn.Influencer'),
      cell: ({ row }) => (
        <TableCell.Link
          label={row.original.influencerName}
          className="flex min-w-25 items-center"
          {...generateProfileLink(row.original.influencerId)}
        >
          <InfluencerAvatar size="40px" src={row.original.influencerAvatar} asChild />
          <TableCell.Text className="ml-2 min-w-25" value={row.original.influencerName} />
        </TableCell.Link>
      )
    },
    {
      accessorKey: 'post',
      header: t<string>('HeaderColumn.Post'),
      cell: ({ row }) => (
        <PostsSection
          size="40px"
          influencerId={row.original.influencerId}
          posts={row.original.thumbnails.map((thumbnail) => ({ thumbNail: thumbnail }))}
          css={{
            width: 'auto',
            maxWidth: '144px',
            '.post-section': {
              height: '60px',
              display: 'flex',
              alignItems: 'center'
            }
          }}
        />
      )
    },
    {
      accessorKey: 'content',
      header: '', // post content doesn't have a title
      cell: ({ row }) => <TableCell.Text className="max-w-50" value={row.original.content || '-'} />
    },
    {
      accessorKey: 'postedDate',
      header: t<string>('HeaderColumn.Post date'),
      cell: ({ row }) => <TableCell.Date value={row.original.postedDate} />
    }
  ];

  return (
    <>
      {data?.marketplacePostsNoRevenue?.count ? (
        <div className="flex items-center gap-1">
          <span
            role="img"
            aria-label="yellow warning emoji"
            css={{
              fontFamily: 'Roboto, sans-serif' // it will add color to ⚠️ emoji
            }}
          >
            ⚠️
          </span>
          <Trans
            i18nKey="posts have 0 revenue"
            count={data?.marketplacePostsNoRevenue?.count}
            components={{ b: <b /> }}
          />
        </div>
      ) : null}
      {children}

      {data?.marketplacePostsNoRevenue?.count ? (
        <TableV2
          rowHeight="61px"
          hasBorder={false}
          maxHeight="290px"
          columns={columns}
          data={data?.marketplacePostsNoRevenue?.posts}
          className="mt-4 rounded-sm border border-border"
          stickyHeader
        />
      ) : null}
    </>
  );
};
